import React, { useState, useEffect, useRef } from 'react';
import { formatearPrecioConPuntos, parsearPrecioFormateado } from '../../utils/facturaFormatters';

interface InputPrecioFormateadoProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  placeholder?: string;
  ariaLabel?: string;
  min?: number;
  step?: number;
  disabled?: boolean;
}

/**
 * Input que formatea precios con separador de miles (punto)
 * mientras el usuario escribe. Mantiene el valor numérico para cálculos.
 *
 * Comportamiento:
 * - Usuario escribe "1000" → se muestra "1.000"
 * - Usuario escribe "10000" → se muestra "10.000"
 * - El valor interno sigue siendo numérico para cálculos
 */
const InputPrecioFormateado: React.FC<InputPrecioFormateadoProps> = ({
  value,
  onChange,
  className = '',
  placeholder = '0',
  ariaLabel,
  min: _min = 0,
  step: _step = 0.01,
  disabled = false,
}) => {
  const [valorDisplay, setValorDisplay] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const cursorPosRef = useRef<number>(0);

  // Sincronizar valor display con prop value cuando cambia externamente
  useEffect(() => {
    const formateado = value > 0 ? formatearPrecioConPuntos(value) : '';
    setValorDisplay(formateado);
  }, [value]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const cursorPos = e.target.selectionStart || 0;

    // Permitir solo números, puntos y comas
    const valorLimpio = inputValue.replace(/[^\d.,]/g, '');

    // Parsear a número
    const numeroParsado = parsearPrecioFormateado(valorLimpio);

    // Actualizar el valor numérico en el estado padre
    onChange(numeroParsado);

    // Formatear para display
    const formateado = numeroParsado > 0 ? formatearPrecioConPuntos(numeroParsado) : '';
    setValorDisplay(formateado);

    // Calcular nueva posición del cursor
    // Contar cuántos puntos había antes vs después del cursor
    const puntosAntes = (inputValue.substring(0, cursorPos).match(/\./g) || []).length;
    const puntosDespues = (formateado.substring(0, cursorPos).match(/\./g) || []).length;
    const ajusteCursor = puntosDespues - puntosAntes;
    cursorPosRef.current = cursorPos + ajusteCursor;
  };

  // Restaurar posición del cursor después del formateo
  useEffect(() => {
    if (inputRef.current && cursorPosRef.current > 0) {
      inputRef.current.setSelectionRange(cursorPosRef.current, cursorPosRef.current);
    }
  }, [valorDisplay]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Permitir teclas especiales: backspace, delete, tab, escape, enter, flechas
    if (
      e.key === 'Backspace' ||
      e.key === 'Delete' ||
      e.key === 'Tab' ||
      e.key === 'Escape' ||
      e.key === 'Enter' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight' ||
      e.key === 'Home' ||
      e.key === 'End'
    ) {
      return;
    }

    // Permitir ctrl/cmd + A, C, V, X
    if (
      (e.ctrlKey || e.metaKey) &&
      (e.key === 'a' || e.key === 'c' || e.key === 'v' || e.key === 'x')
    ) {
      return;
    }

    // Permitir números, punto y coma
    if (!/^[0-9.,]$/.test(e.key)) {
      e.preventDefault();
    }
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    // Seleccionar todo el texto al enfocar para facilitar la edición
    e.target.select();
  };

  const defaultClassName = `px-2 py-1 border border-gray-300 dark:border-gray-600 rounded text-right
                           bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100
                           focus:ring-2 focus:ring-blue-500 focus:border-blue-500`;

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode="decimal"
      value={valorDisplay}
      onChange={handleChange}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      placeholder={placeholder}
      className={`${defaultClassName} ${className}`}
      aria-label={ariaLabel}
      disabled={disabled}
      autoComplete="off"
    />
  );
};

export default InputPrecioFormateado;
